/**
 * CampusPostCard: renderiza un post del muro (anuncio, encuesta, material) con
 * su barra de reacciones, comentarios anidados e input.
 *
 * Mantiene el JSX a una profundidad de anidación manejable delegando en:
 *   - CampusPoll              (bloque de encuesta)
 *   - CampusReactionsBar      (reacciones + toggle comentarios)
 *   - CampusCommentsSection   (árbol de comentarios + input)
 */
import React from 'react';
import axios from 'axios';
import { Pin, Paperclip, Trash2, Edit } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import CampusPoll from './CampusPoll';
import CampusReactionsBar from './CampusReactionsBar';
import CampusCommentsSection from './CampusCommentsSection';
import LinkifiedText from './LinkifiedText';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function PostHeader({ post, ctx, puedeEditar }) {
  return (
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center text-violet-600 font-semibold text-sm">
          {(post.autor?.nombre?.[0] || 'U').toUpperCase()}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">
              {post.autor?.nombre} {post.autor?.apellido}
            </span>
            {post.fijado && <Pin className="w-3.5 h-3.5 text-amber-500" />}
            <Badge variant="outline" className="text-xs capitalize">{post.tipo}</Badge>
          </div>
          <span className="text-xs text-gray-400">{ctx.formatFecha(post.created_at)}</span>
        </div>
      </div>
      {puedeEditar && post.tipo !== 'encuesta' && (
        <button
          onClick={() => ctx.iniciarEdicionPost(post)}
          className="text-gray-400 hover:text-violet-600 p-1"
          title="Editar"
          data-testid={`editar-post-${post.id}`}
        >
          <Edit className="w-4 h-4" />
        </button>
      )}
      {puedeEditar && (
        <button
          onClick={() => ctx.eliminarPost(post.id)}
          className="text-gray-400 hover:text-red-500 p-1"
          title="Eliminar"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function PostAttachments({ post, ctx }) {
  if (!post.archivos_adjuntos || post.archivos_adjuntos.length === 0) return null;
  const trackDescarga = (nombre) => {
    axios.post(
      `${API_URL}/campus/actividad/track`,
      { space_id: ctx.spaceId, tipo: 'descarga_material', metadata: { nombre } },
      { headers: { Authorization: `Bearer ${ctx.token}` } },
    ).catch(() => {});
  };
  return (
    <div className="flex gap-2 mt-3 flex-wrap">
      {post.archivos_adjuntos.map((a, i) => (
        <a
          key={i}
          href={a.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackDescarga(a.nombre)}
          className="flex items-center gap-1 text-xs text-violet-600 bg-violet-50 px-2 py-1 rounded"
        >
          <Paperclip className="w-3 h-3" />
          {a.nombre || 'Archivo'}
        </a>
      ))}
    </div>
  );
}

export default function CampusPostCard({ post, ctx }) {
  const isCommentsOpen = ctx.expandedComments[post.id];
  const esAutor = post.autor_id === ctx.user?.id;
  const puedeEditar = esAutor || ctx.esCoordinadorODocente;

  return (
    <Card data-testid={`post-${post.id}`} className="overflow-hidden">
      <CardContent className="p-4">
        <PostHeader post={post} ctx={ctx} puedeEditar={puedeEditar} />

        <h3 className="font-semibold mt-3">{post.titulo}</h3>
        {post.tipo !== 'encuesta' && (
          <p className="text-sm text-gray-600 mt-1"><LinkifiedText>{post.contenido}</LinkifiedText></p>
        )}

        {post.tipo === 'encuesta' && post.encuesta && (
          <CampusPoll post={post} ctx={ctx} />
        )}

        <PostAttachments post={post} ctx={ctx} />

        <CampusReactionsBar post={post} ctx={ctx} />

        {isCommentsOpen && <CampusCommentsSection post={post} ctx={ctx} />}
      </CardContent>
    </Card>
  );
}
